import { useCallback, useMemo, useReducer } from "react";

/**
 * Generic hot-seat match state.
 *
 * Owns the players, whose turn it is, per-player numeric stat (score OR HP
 * depending on the game), and overall match status. Game-specific rules
 * (rounds, win conditions) live in each game's engine - this hook just
 * tracks the shared scaffolding so every game looks and feels consistent.
 */

export type PlayerId = 0 | 1;

export type Player = {
  id: PlayerId;
  name: string;
};

export type MatchStatus = "setup" | "playing" | "finished";

export type HotSeatState = {
  players: [Player, Player];
  /** Per-player numeric stat. Game decides what it means (score, HP, rounds won). */
  stats: [number, number];
  currentTurn: PlayerId;
  status: MatchStatus;
  /** Optional winner once status === "finished". null = draw. */
  winner: PlayerId | null;
  /** Monotonically increasing turn counter for analytics / sudden-death rules. */
  turnNumber: number;
};

type Action =
  | { type: "start"; players: [Player, Player]; initialStat: number }
  | { type: "setStat"; player: PlayerId; value: number }
  | { type: "adjustStat"; player: PlayerId; delta: number }
  | { type: "nextTurn" }
  | { type: "finish"; winner: PlayerId | null }
  | { type: "reset" };

const INITIAL: HotSeatState = {
  players: [
    { id: 0, name: "Player 1" },
    { id: 1, name: "Player 2" },
  ],
  stats: [0, 0],
  currentTurn: 0,
  status: "setup",
  winner: null,
  turnNumber: 0,
};

function reducer(state: HotSeatState, action: Action): HotSeatState {
  switch (action.type) {
    case "start":
      return {
        players: action.players,
        stats: [action.initialStat, action.initialStat],
        currentTurn: 0,
        status: "playing",
        winner: null,
        turnNumber: 1,
      };
    case "setStat": {
      const stats: [number, number] = [...state.stats] as [number, number];
      stats[action.player] = action.value;
      return { ...state, stats };
    }
    case "adjustStat": {
      const stats: [number, number] = [...state.stats] as [number, number];
      stats[action.player] = stats[action.player] + action.delta;
      return { ...state, stats };
    }
    case "nextTurn":
      return {
        ...state,
        currentTurn: state.currentTurn === 0 ? 1 : 0,
        turnNumber: state.turnNumber + 1,
      };
    case "finish":
      return { ...state, status: "finished", winner: action.winner };
    case "reset":
      return INITIAL;
    default:
      return state;
  }
}

export type UseHotSeatMatch = ReturnType<typeof useHotSeatMatch>;

export function useHotSeatMatch(initialStat = 0) {
  const [state, dispatch] = useReducer(reducer, INITIAL);

  const start = useCallback(
    (p1Name: string, p2Name: string) => {
      const cleanName = (raw: string, fallback: string) => {
        const trimmed = raw.trim().slice(0, 24);
        return trimmed.length > 0 ? trimmed : fallback;
      };
      dispatch({
        type: "start",
        players: [
          { id: 0, name: cleanName(p1Name, "Player 1") },
          { id: 1, name: cleanName(p2Name, "Player 2") },
        ],
        initialStat,
      });
    },
    [initialStat],
  );

  const setStat = useCallback(
    (player: PlayerId, value: number) => dispatch({ type: "setStat", player, value }),
    [],
  );

  const adjustStat = useCallback(
    (player: PlayerId, delta: number) => dispatch({ type: "adjustStat", player, delta }),
    [],
  );

  const nextTurn = useCallback(() => dispatch({ type: "nextTurn" }), []);
  const finish = useCallback(
    (winner: PlayerId | null) => dispatch({ type: "finish", winner }),
    [],
  );
  const reset = useCallback(() => dispatch({ type: "reset" }), []);

  const currentPlayer = useMemo(
    () => state.players[state.currentTurn],
    [state.players, state.currentTurn],
  );
  const otherPlayer = useMemo(
    () => state.players[state.currentTurn === 0 ? 1 : 0],
    [state.players, state.currentTurn],
  );

  return {
    state,
    currentPlayer,
    otherPlayer,
    start,
    setStat,
    adjustStat,
    nextTurn,
    finish,
    reset,
  };
}